/**
 * Frame22 – Receive death certificates (IAD)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, PackageOpen } from 'lucide-react';
import { Button } from '../ui/Button';
import { SettlementLayout } from '../settlement/SettlementLayout';
import DocumentUploadSection from '../settlement/DocumentUploadSection';
import { useSettlementFrameData } from '../../hooks/useSettlementFrameData';
import { useDeathCertDocuments } from '../../hooks/useDeathCertDocuments';
import {
  DEATH_CERT_DOC,
  DEATH_CERT_FRAME_DATA,
  deathCertRoute,
} from '../../utils/deathCertWorkflow';

export default function Frame22() {
  const navigate = useNavigate();
  const { estateId } = useParams<{ estateId?: string }>();
  const { data, save, loading } = useSettlementFrameData(estateId ?? null, DEATH_CERT_FRAME_DATA.received);
  const { documents, onUpload, onDelete } = useDeathCertDocuments(estateId, 'death-cert-doc:22');

  const [copiesReceived, setCopiesReceived] = useState('');
  const [dateReceived, setDateReceived] = useState('');
  const [matchOrderedCount, setMatchOrderedCount] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    setCopiesReceived(data.copiesReceived != null ? String(data.copiesReceived) : '');
    setDateReceived(String(data.dateReceived || ''));
    setMatchOrderedCount(Boolean(data.matchOrderedCount));
    setNotes(String(data.notes || ''));
  }, [data]);

  const persist = async () => {
    await save({
      copiesReceived: copiesReceived === '' ? '' : Number(copiesReceived),
      dateReceived,
      matchOrderedCount,
      notes,
    });
  };

  return (
    <SettlementLayout currentPhase="iad" currentStep="death-certificates" estateId={estateId ?? null}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-start gap-3">
          <PackageOpen className="w-8 h-8 text-cyan-700 shrink-0 mt-1" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Receive Certificates</h1>
            <p className="text-gray-600">
              Confirm how many certified copies you received, when they arrived, and store scans for your records.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">Certified copies received</label>
              <input
                type="number"
                min={0}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={copiesReceived}
                onChange={(e) => setCopiesReceived(e.target.value)}
                onBlur={() => void persist()}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">Date received</label>
              <input
                type="date"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={dateReceived}
                onChange={(e) => setDateReceived(e.target.value)}
                onBlur={() => void persist()}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={matchOrderedCount}
              onChange={(e) => {
                setMatchOrderedCount(e.target.checked);
                void save({
                  copiesReceived: copiesReceived === '' ? '' : Number(copiesReceived),
                  dateReceived,
                  matchOrderedCount: e.target.checked,
                  notes,
                });
              }}
            />
            Count matches what I ordered
          </label>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Notes</label>
            <textarea
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm min-h-[80px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => void persist()}
              placeholder="Discrepancies, spelling corrections needed, extra copies, etc."
            />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8 shadow-sm">
          <DocumentUploadSection
            documents={documents}
            onUpload={async (file) => {
              await onUpload(file, DEATH_CERT_DOC.certifiedScan);
            }}
            onDelete={(id) => onDelete(id)}
            disabled={!estateId}
          />
          <p className="text-xs text-gray-500 mt-2">
            Optional: upload redacted scans of certified copies for your working files. originals should stay in a secure
            place.
          </p>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => navigate(deathCertRoute('/frame-21', estateId))} disabled={loading}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={() => {
              void persist().then(() => navigate(deathCertRoute('/frame-23', estateId)));
            }}
            size="lg"
            disabled={loading}
          >
            Distribute to institutions
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </SettlementLayout>
  );
}
